import { Button, message, Popconfirm, Tag, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useMemo } from 'react'
import { blue } from '../../color'
import AccessControlAction from '../../components/AccessControlAction'
import {
  EyeOutlineIcon,
  MoveToFolderOutlineIcon,
  TrashBin2OutlineIcon,
} from '../../components/AppIcon'
import { AppTable } from '../../components/AppTable/AppTable'
import { EmptyText, WordBreak } from '../../components/Common/CellText'
import { ACL_ACTION_TYPE } from '../../constants'
import utils from '../../utils'
import MissionItemDetailModalV2 from './MissionItemDetailModalV2'
import { OverlayPanelWrapper } from '../../components/Overlays/OverlayPanelWrapper'

const MissionListV2 = props => {
  const {
    taskStore,
    textLoading,
    loadingList,
    setLoadingList,
    activeTab,
    handleNextMonth,
    showInMissionGroupPage,
  } = props

  const { taskList } = taskStore

  const handleDeleteTask = async codeTask => {
    try {
      setLoadingList(true)
      await taskStore.deleteTask(codeTask)
      await taskStore.getTaskList()
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra')
    } finally {
      message.success('Đã xóa nhiệm vụ thành công!')
      setLoadingList(false)
    }
  }

  const filteredTasks = useMemo(() => {
    return (taskList || []).filter(task => {
      return task?.isUser !== true
    })
  }, [taskList])

  const columns = [
    {
      title: 'Phòng ban',
      dataIndex: ['cap_giao', 'ten_cap_giao'],
      key: 'ten_cap_giao',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Nhiệm vụ',
      dataIndex: 'noi_dung',
      key: 'noi_dung',
      ellipsis: true,
      width: 350,
      render: text =>
        text ? (
          utils.removeHtml(text)
        ) : (
          <EmptyText>Không có nội dung</EmptyText>
        ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'ghi_chu',
      key: 'ghi_chu',
      ellipsis: true,
      width: 220,
      render: text =>
        text ? utils.removeHtml(text) : <EmptyText>Không có ghi chú</EmptyText>,
    },
    {
      title: 'Ngày bắt đầu',
      dataIndex: 'ngay_bat_dau',
      key: 'ngay_bat_dau',
      ellipsis: true,
      width: 150,
      render: value =>
        value ? moment(value).format('DD/MM/YYYY') : 'Không rõ',
    },
    {
      title: 'Ngày kết thúc',
      dataIndex: 'ngay_ket_thuc',
      key: 'ngay_ket_thuc',
      ellipsis: true,
      width: 150,
      render: value =>
        value ? moment(value).format('DD/MM/YYYY') : 'Không rõ',
    },
    {
      title: 'Trạng thái',
      dataIndex: ['ket_qua', 'phan_tram_hoan_thanh'],
      key: 'status',
      width: 140,
      render: value =>
        value === '100' ? (
          <Tag color="green" style={{ borderRadius: 4, border: 'none' }}>
            Hoàn thành
          </Tag>
        ) : (
          <Tag color="red" style={{ borderRadius: 4, border: 'none' }}>
            Chưa hoàn thành
          </Tag>
        ),
    },
    {
      title: 'Văn bản liên kết',
      dataIndex: 'van_bans',
      key: 'van_bans',
      width: 220,
      ellipsis: true,
      render: values =>
        values?.length > 0 ? (
          (values || [])
            .map(item => `${item.document_number} - ${item.ten_van_ban}`)
            .join(', ')
        ) : (
          <EmptyText>Không có văn bản</EmptyText>
        ),
    },
    {
      title: 'Tài liệu đính kèm VB',
      dataIndex: 'file_dinh_kem_vb',
      key: 'file_dinh_kem_vb',
      width: 200,
      ellipsis: true,
      render: values =>
        values?.length > 0 ? (
          (values || []).map(item => item.file_title).join(', ')
        ) : (
          <EmptyText>Không có tài liệu đính kèm văn bản</EmptyText>
        ),
    },
    {
      title: 'Nội dung kết quả',
      dataIndex: ['ket_qua', 'noi_dung_ket_qua_giao'],
      key: 'resultContent',
      ellipsis: true,
      width: 200,
      render: value =>
        value ? (
          utils.removeHtml(value)
        ) : (
          <EmptyText>Không có nội dung kết quả</EmptyText>
        ),
    },
    {
      title: 'Kiến nghị',
      dataIndex: ['ket_qua', 'kien_nghi'],
      key: 'recommendation',
      ellipsis: true,
      width: 200,
      render: value =>
        value ? (
          utils.removeHtml(value)
        ) : (
          <EmptyText>Không có kiến nghị</EmptyText>
        ),
    },
    {
      title: 'Tài liệu',
      dataIndex: ['ket_qua', 'files'],
      key: 'otherDocument',
      ellipsis: true,
      width: 200,
      render: values =>
        values?.length > 0 ? (
          (values || []).map(item => item.file_title).join(', ')
        ) : (
          <EmptyText>Không có tài liệu</EmptyText>
        ),
    },

    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <OverlayPanelWrapper
            renderOverlayPanel={(open, setOpen) => (
              <MissionItemDetailModalV2
                open={open}
                setOpen={setOpen}
                taskCode={record.task_code}
              />
            )}>
            <Tooltip placement="bottomRight" title="Chi tiết nhiệm vụ">
              <Button
                type="text"
                shape="circle"
                icon={
                  <EyeOutlineIcon
                    style={{ width: 16, height: 16, color: blue }}
                  />
                }
              />
            </Tooltip>
          </OverlayPanelWrapper>

          <AccessControlAction
            aclActionType={ACL_ACTION_TYPE.chuyen_sang_thang__MISSION}>
            <Popconfirm
              title="Bạn có muốn chuyển nhiệm vụ này sang tháng sau?"
              onConfirm={e => {
                e.stopPropagation()
                handleNextMonth(record.task_code)
              }}
              okText="Đồng ý"
              cancelText="Không">
              <Tooltip
                placement="bottomRight"
                title="Chuyển nhiệm vụ sang tháng sau">
                <Button
                  onClick={e => e.stopPropagation()}
                  type="text"
                  shape="circle"
                  icon={
                    <MoveToFolderOutlineIcon
                      style={{ width: 16, height: 16, color: blue }}
                    />
                  }
                />
              </Tooltip>
            </Popconfirm>
          </AccessControlAction>

          <AccessControlAction aclActionType={ACL_ACTION_TYPE.delete__MISSION}>
            <Popconfirm
              title="Bạn có muốn xóa nhiệm vụ này?"
              onConfirm={async e => {
                e.stopPropagation()
                await handleDeleteTask(record.task_code)
              }}
              onCancel={e => e.stopPropagation()}
              okText="Đồng ý"
              cancelText="Không"
              placement="topRight">
              <Tooltip placement="bottomRight" title="Xóa nhiệm vụ">
                <Button
                  onClick={e => e.stopPropagation()}
                  danger
                  type="text"
                  shape="circle"
                  icon={
                    <TrashBin2OutlineIcon style={{ width: 16, height: 16 }} />
                  }
                />
              </Tooltip>
            </Popconfirm>
          </AccessControlAction>
        </div>
      ),
    },
  ]
  return (
    <>
      <AppTable
        dataSource={filteredTasks}
        columns={columns}
        rowKey="task_code"
        loading={loadingList}
      />
    </>
  )
}

export default inject(
  'taskStore',
  'authenticationStore'
)(observer(MissionListV2))
